import React, { useEffect, useState } from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import { Modal, Box, Typography, Button } from '@mui/material';
import cytoscape from 'cytoscape';
import coseBilkent from 'cytoscape-cose-bilkent';

cytoscape.use(coseBilkent);

interface NodeProperties {
    [key: string]: any;
}

interface GraphNode {
    id: string;
    label: string;
    properties?: NodeProperties;
}

interface GraphLink {
    source: string | GraphNode;
    target: string | GraphNode;
    relationship: string;
}

interface GraphData {
    nodes: GraphNode[];
    links: GraphLink[];
}

interface FraudGraphProps {
    graphData: GraphData;
}

interface NodeData {
    id: string;
    label: string;
    nodeType: string;
    properties?: NodeProperties;
    flagged: boolean; // Ensure 'flagged' is consistently lowercase
    [key: string]: any; // Other properties
}

interface EdgeData {
    id: string;
    source: string;
    target: string;
    label: string;
    relationshipType: string;
    [key: string]: any; // Other properties
}

interface SelectedEdgeData extends EdgeData {
    sourceNode: NodeData;
    targetNode: NodeData;
}

const FraudGraph: React.FC<FraudGraphProps> = ({ graphData }) => {
    const [elements, setElements] = useState<any[]>([]);
    const [selectedNode, setSelectedNode] = useState<NodeData | null>(null);
    const [selectedEdge, setSelectedEdge] = useState<SelectedEdgeData | null>(null);
    const [openModal, setOpenModal] = useState<boolean>(false);

    useEffect(() => {
        const nodes = graphData.nodes.map((node) => {
            const nodeLabel = node.label;
            const displayLabel = `${nodeLabel} (${
                node.properties?.Email || node.properties?.Name || node.properties?.Id || node.id
            })`;

            // Extract flagged value and store it at the top level in data
            const isFlagged = node.properties?.Flagged ? 'true' : 'false';

            return {
                data: {
                    id: node.id,
                    label: displayLabel,
                    nodeType: node.label,
                    flagged: isFlagged, // Top-level flagged property
                    properties: node.properties,
                    ...node.properties
                }
            };
        });

        const edges = graphData.links.map((link, index) => ({
            data: {
                id: 'e' + index,
                source: typeof link.source === 'string' ? link.source : link.source.id,
                target: typeof link.target === 'string' ? link.target : link.target.id,
                label: link.relationship,
                relationshipType: link.relationship
            }
        }));
        console.log(nodes);
        setElements([...nodes, ...edges]);
    }, [graphData]);

    const layout = {
        name: 'cose-bilkent',
        idealEdgeLength: 200,
        nodeRepulsion: 8000,
        edgeElasticity: 0.1,
        gravity: 0.1,
        numIter: 2500,
        gravityRangeCompound: 1.5,
        gravityCompound: 1.0,
        gravityRange: 3.8,
        animate: 'end',
        animationDuration: 1500,
        fit: true,
        padding: 50
    };

    const stylesheet = [
        {
            selector: 'node',
            style: {
                'background-color': 'gray',
                label: 'data(label)',
                'text-valign': 'bottom',
                'text-halign': 'center',
                color: 'black',
                'font-size': 12,
                width: 40,
                height: 40,
                'text-margin-y': 10
            }
        },
        {
            selector: 'node[nodeType = "USER"]',
            style: { 'background-color': 'blue' }
        },
        {
            selector: 'node[nodeType = "DEVICE"]',
            style: { 'background-color': 'green' }
        },
        {
            selector: 'node[nodeType = "JURISTIC_ENTITY"]',
            style: { 'background-color': 'yellow' }
        },
        {
            selector: 'node[nodeType = "PHONE_NUMBER"]',
            style: { 'background-color': 'purple' }
        },
        {
            selector: 'node[nodeType = "ID_NUMBER"]',
            style: { 'background-color': 'orange' }
        },
        {
            selector: 'node[nodeType = "REGISTRATION_NUMBER"]',
            style: { 'background-color': 'brown' }
        },
        // Flagged nodes - red border
        {
            selector: 'node[flagged = "true"]',
            style: {
                'background-color': 'red',
                'border-width': 6,
                'border-color': 'darkred',
                width: 60, // Increase the size
                height: 60,
                'shadow-blur': 10, // Adding a glow effect
                'shadow-color': 'darkred',
                'shadow-opacity': 0.8,
                'shadow-offset-x': 0,
                'shadow-offset-y': 0
            }
        },
        {
            selector: 'edge',
            style: {
                width: 2,
                'line-color': '#ccc',
                'target-arrow-color': '#ccc',
                'target-arrow-shape': 'triangle',
                label: 'data(label)',
                'font-size': 10,
                'text-rotation': 'autorotate',
                'curve-style': 'bezier'
            }
        }
    ];

    const handleNodeClick = (event: any) => {
        event.stopPropagation();
        const node = event.target;
        setSelectedNode(node.data() as NodeData);
        setSelectedEdge(null);
        setOpenModal(true);
    };

    const handleEdgeClick = (event: any) => {
        event.stopPropagation();
        const edge = event.target;
        const edgeData = edge.data() as EdgeData;
        const sourceNode = edge.source().data() as NodeData;
        const targetNode = edge.target().data() as NodeData;
        setSelectedEdge({ ...edgeData, sourceNode, targetNode } as SelectedEdgeData);
        setSelectedNode(null);
        setOpenModal(true);
    };

    const modalStyle = {
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: 400,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4
    };

    return (
        <>
            <CytoscapeComponent
                key={elements.length}
                elements={elements}
                style={{ width: '100%', height: '724px', minHeight: '100%' }}
                layout={layout}
                stylesheet={stylesheet}
                cy={(cy) => {
                    cy.on('click', 'node', handleNodeClick);
                    cy.on('tap', 'node', handleNodeClick);
                    cy.on('click', 'edge', handleEdgeClick);
                    cy.on('tap', 'edge', handleEdgeClick);
                }}
            />
            <Modal
                open={openModal}
                onClose={() => {
                    setOpenModal(false);
                    setSelectedNode(null);
                    setSelectedEdge(null);
                }}
                aria-labelledby="element-properties-title"
                aria-describedby="element-properties-description"
            >
                <Box sx={modalStyle}>
                    {(selectedNode || selectedEdge) && (
                        <>
                            {selectedNode && (
                                <>
                                    <Typography id="element-properties-title" variant="h6" component="h2">
                                        {selectedNode.nodeType} Properties
                                    </Typography>
                                    <Typography id="element-properties-description" sx={{ mt: 2 }}>
                                        <ul>
                                            {Object.entries(selectedNode.properties || {}).map(([key, value]) => (
                                                <li key={key}>
                                                    <strong>{key}:</strong> {String(value)}
                                                </li>
                                            ))}
                                        </ul>
                                    </Typography>
                                </>
                            )}
                            {selectedEdge && (
                                <>
                                    <Typography id="element-properties-title" variant="h6" component="h2">
                                        Edge Properties
                                    </Typography>
                                    <Typography id="element-properties-description" sx={{ mt: 2 }}>
                                        <ul>
                                            {Object.entries(selectedEdge)
                                                .filter(
                                                    ([key]) =>
                                                        ![
                                                            'id',
                                                            'label',
                                                            'source',
                                                            'target',
                                                            'sourceNode',
                                                            'targetNode'
                                                        ].includes(key)
                                                )
                                                .map(([key, value]) => (
                                                    <li key={key}>
                                                        <strong>{key}:</strong> {String(value)}
                                                    </li>
                                                ))}
                                            <li>
                                                <strong>Source Node:</strong> {selectedEdge.sourceNode.nodeType} (
                                                {selectedEdge.sourceNode.properties?.Id || selectedEdge.sourceNode.id})
                                            </li>
                                            <li>
                                                <strong>Target Node:</strong> {selectedEdge.targetNode.nodeType} (
                                                {selectedEdge.targetNode.properties?.Id || selectedEdge.targetNode.id})
                                            </li>
                                        </ul>
                                    </Typography>
                                </>
                            )}
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    setOpenModal(false);
                                    setSelectedNode(null);
                                    setSelectedEdge(null);
                                }}
                                sx={{ mt: 2 }}
                            >
                                Close
                            </Button>
                        </>
                    )}
                </Box>
            </Modal>
        </>
    );
};

export default FraudGraph;
